import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '@/components/ui/chart';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { AnalyticsUsageResponse } from '@octave/contracts';
import {
  BarChart3,
  Clock,
  Download,
  HardDrive,
  Loader2,
  MessageSquare,
  Phone,
  TrendingUp,
} from 'lucide-react';
import { useState } from 'react';
import { formatNumber } from '@/lib/format';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  LabelList,
  ReferenceLine,
  XAxis,
  YAxis,
} from 'recharts';

// ─── Color config per product type ─────────────────────

const CHART_COLORS = {
  voice: { main: '#10b981', light: '#34d399', dark: '#059669', bg: 'bg-emerald-50', text: 'text-emerald-600' },
  data: { main: '#0ea5e9', light: '#38bdf8', dark: '#0284c7', bg: 'bg-sky-50', text: 'text-sky-600' },
  'sms-mms': { main: '#f59e0b', light: '#fbbf24', dark: '#d97706', bg: 'bg-amber-50', text: 'text-amber-600' },
} as const;

const CHART_LABELS = {
  voice: 'Voix (min)',
  data: 'Data (Go)',
  'sms-mms': 'SMS / MMS',
} as const;

const PRODUCT_LABELS = {
  voice: 'Voix',
  data: 'Data',
  'sms-mms': 'SMS / MMS',
} as const;

// ─── Helpers ────────────────────────────────────────────

function formatYAxis(value: number): string {
  if (value >= 1000) return `${(value / 1000).toFixed(value >= 10000 ? 0 : 1)}k`;
  return formatNumber(value);
}

// ─── Tooltip ────────────────────────────────────────────

interface VoiceTooltipPayloadEntry {
  payload?: { label?: string; value?: number; quantity?: number };
}

interface VoiceTooltipProps {
  active?: boolean;
  payload?: VoiceTooltipPayloadEntry[];
}

function VoiceTooltipContent({ active, payload }: VoiceTooltipProps) {
  if (!active || !payload?.length) return null;
  const data = payload[0]?.payload;
  const minutes = data?.value ?? 0;
  const callCount = data?.quantity ?? 0;

  return (
    <div className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-xs shadow-lg min-w-[11rem]">
      <div className="font-semibold text-slate-900 mb-2">{data?.label}</div>
      <div className="space-y-1.5">
        <div className="flex items-center justify-between gap-6">
          <div className="flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full bg-emerald-500" />
            <span className="text-slate-500">Durée</span>
          </div>
          <span className="font-mono font-semibold text-slate-900 tabular-nums">
            {formatNumber(minutes)} min
          </span>
        </div>
        {callCount > 0 && (
          <div className="flex items-center justify-between gap-6">
            <div className="flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full bg-emerald-300" />
              <span className="text-slate-500">Appels</span>
            </div>
            <span className="font-mono font-semibold text-slate-900 tabular-nums">
              {formatNumber(callCount)}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Props ──────────────────────────────────────────────

interface ConsumptionChartProps {
  data?: AnalyticsUsageResponse;
  isLoading?: boolean;
  productType: 'data' | 'voice' | 'sms-mms';
  onExport?: (format: 'xlsx' | 'csv') => void;
  isExporting?: boolean;
}

// ─── Component ──────────────────────────────────────────

export function ConsumptionChart({
  data,
  isLoading,
  productType,
  onExport,
  isExporting,
}: ConsumptionChartProps) {
  const [chartType, setChartType] = useState<'bar' | 'curve'>('bar');
  const [showLimit, setShowLimit] = useState(false);

  const items = data?.items ?? [];
  const colors = CHART_COLORS[productType];
  const chartLabel = CHART_LABELS[productType];

  const hasDataLimit =
    productType === 'data' &&
    !!data?.dataSummary?.dataLimitMo &&
    data.dataSummary.dataLimitMo > 0;
  const dataLimitGo = hasDataLimit
    ? (data!.dataSummary!.dataLimitMo as number) / 1000
    : undefined;
  const maxItemValue = items.reduce((m, it) => Math.max(m, it.value ?? 0), 0);
  const yDomain: [number, number] | undefined =
    showLimit && dataLimitGo
      ? [0, Math.max(maxItemValue, dataLimitGo) * 1.05]
      : undefined;

  const chartConfig = {
    value: { label: chartLabel, color: colors.main },
  } satisfies ChartConfig;

  // ─── Loading state ──────────────────────────────────

  if (isLoading) {
    return (
      <div className="space-y-5">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="rounded-2xl bg-white p-5 shadow-sm animate-pulse">
              <div className="h-3 w-20 bg-slate-100 rounded mb-3" />
              <div className="h-7 w-16 bg-slate-100 rounded" />
            </div>
          ))}
        </div>
        <Card className="rounded-2xl shadow-sm">
          <CardContent className="pt-6">
            <div className="h-[350px] flex items-center justify-center">
              <div className="flex flex-col items-center gap-3">
                <Loader2 className="h-6 w-6 animate-spin text-emerald-500" />
                <p className="text-sm text-slate-400">Chargement...</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ─── Empty state ────────────────────────────────────

  if (items.length === 0) {
    return (
      <div className="rounded-2xl bg-white shadow-sm py-20 flex flex-col items-center justify-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-slate-100 mb-4">
          <BarChart3 className="h-8 w-8 text-slate-300" />
        </div>
        <p className="text-slate-500 text-base font-medium">
          {!data ? 'Sélectionnez une ligne' : 'Aucune donnée disponible'}
        </p>
        <p className="text-slate-400 text-sm mt-1">
          {!data
            ? 'Choisissez une ligne dans les filtres pour afficher son historique'
            : 'Modifiez les filtres pour voir les données'}
        </p>
      </div>
    );
  }

  // ─── Summary cards ──────────────────────────────────

  const summaryCards = buildSummaryCards(data, productType);

  return (
    <div className="space-y-5">
      {/* Stat cards — above the chart */}
      {summaryCards.length > 0 && (
        <div className={`grid gap-4 ${summaryCards.length === 1 ? 'grid-cols-1 max-w-xs' : summaryCards.length === 2 ? 'grid-cols-2 max-w-lg' : 'grid-cols-2 sm:grid-cols-3'}`}>
          {summaryCards.map((card) => (
            <div
              key={card.label}
              className="flex items-center gap-3 rounded-2xl bg-white p-5 shadow-sm"
            >
              <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${card.iconBg}`}>
                <card.icon className={`h-4 w-4 ${card.iconColor}`} />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-slate-400 truncate">{card.label}</p>
                <p className="text-xl font-bold text-slate-900 tabular-nums">
                  {card.value}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Chart card */}
      <Card className="rounded-2xl shadow-sm overflow-hidden">
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <div>
            <CardTitle className="text-base font-semibold text-slate-900">
              Historique de consommation
            </CardTitle>
            <CardDescription className="text-xs text-slate-400">
              {PRODUCT_LABELS[productType]}
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            {/* Forfait limit toggle */}
            {hasDataLimit && (
              <button
                onClick={() => setShowLimit((v) => !v)}
                className={`h-8 rounded-lg px-2.5 text-xs font-medium transition-colors ${
                  showLimit
                    ? 'bg-sky-50 text-sky-700 hover:bg-sky-100'
                    : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                }`}
              >
                Limite forfait
              </button>
            )}

            {/* Chart type toggle */}
            <div className="flex items-center gap-0.5 rounded-lg bg-slate-100 p-0.5">
              <button
                onClick={() => setChartType('bar')}
                className={`rounded-md p-1.5 transition-colors ${chartType === 'bar' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-400 hover:text-slate-600'}`}
              >
                <BarChart3 className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={() => setChartType('curve')}
                className={`rounded-md p-1.5 transition-colors ${chartType === 'curve' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-400 hover:text-slate-600'}`}
              >
                <TrendingUp className="h-3.5 w-3.5" />
              </button>
            </div>

            {/* Export */}
            {onExport && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" disabled={isExporting} className="h-8 px-2.5 text-slate-400 hover:text-slate-600">
                    {isExporting ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Download className="h-3.5 w-3.5" />
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => onExport('xlsx')}>
                    Excel (.xlsx)
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onExport('csv')}>
                    CSV (.csv)
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </CardHeader>

        <CardContent className="pt-0">
          <div className="h-[350px] w-full">
            <ChartContainer config={chartConfig} className="h-full w-full">
              {chartType === 'bar' ? (
                <BarChart
                  accessibilityLayer
                  data={items}
                  margin={{ top: 24, right: 12, left: 4, bottom: 0 }}
                >
                  <defs>
                    <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={colors.light} />
                      <stop offset="100%" stopColor={colors.main} />
                    </linearGradient>
                  </defs>
                  <XAxis
                    dataKey="label"
                    tickLine={false}
                    axisLine={false}
                    tick={{ fontSize: 11, fill: '#94a3b8' }}
                    tickMargin={8}
                  />
                  <YAxis
                    tickLine={false}
                    axisLine={false}
                    tick={{ fontSize: 11, fill: '#94a3b8' }}
                    tickMargin={4}
                    tickFormatter={formatYAxis}
                    domain={yDomain}
                    label={{
                      value: chartLabel,
                      angle: -90,
                      position: 'insideLeft',
                      offset: 4,
                      style: { fontSize: 12, fill: '#64748b', fontWeight: 500 },
                    }}
                  />
                  <ChartTooltip
                    content={
                      productType === 'voice' ? (
                        <VoiceTooltipContent />
                      ) : (
                        <ChartTooltipContent
                          indicator="dot"
                          labelFormatter={(label) => label}
                        />
                      )
                    }
                  />
                  {showLimit && dataLimitGo && (
                    <ReferenceLine
                      y={dataLimitGo}
                      stroke={colors.dark}
                      strokeDasharray="4 4"
                      strokeWidth={1.5}
                      label={{
                        value: `Forfait : ${formatYAxis(dataLimitGo)} Go`,
                        position: 'insideTopRight',
                        style: { fontSize: 11, fill: colors.dark, fontWeight: 600 },
                      }}
                    />
                  )}
                  <Bar
                    dataKey="value"
                    fill="url(#barGradient)"
                    radius={[6, 6, 0, 0]}
                    name={chartLabel}
                    maxBarSize={48}
                  >
                    <LabelList
                      dataKey="value"
                      position="top"
                      formatter={formatYAxis}
                      style={{ fontSize: 11, fill: '#64748b', fontWeight: 600 }}
                    />
                  </Bar>
                </BarChart>
              ) : (
                <AreaChart
                  accessibilityLayer
                  data={items}
                  margin={{ top: 12, right: 12, left: 4, bottom: 0 }}
                >
                  <defs>
                    <linearGradient id="areaGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={colors.main} stopOpacity={0.25} />
                      <stop offset="100%" stopColor={colors.main} stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <XAxis
                    dataKey="label"
                    tickLine={false}
                    axisLine={false}
                    tick={{ fontSize: 11, fill: '#94a3b8' }}
                    tickMargin={8}
                  />
                  <YAxis
                    tickLine={false}
                    axisLine={false}
                    tick={{ fontSize: 11, fill: '#94a3b8' }}
                    tickMargin={4}
                    tickFormatter={formatYAxis}
                    domain={yDomain}
                    label={{
                      value: chartLabel,
                      angle: -90,
                      position: 'insideLeft',
                      offset: 4,
                      style: { fontSize: 12, fill: '#64748b', fontWeight: 500 },
                    }}
                  />
                  <ChartTooltip
                    content={
                      productType === 'voice' ? (
                        <VoiceTooltipContent />
                      ) : (
                        <ChartTooltipContent
                          indicator="dot"
                          labelFormatter={(label) => label}
                        />
                      )
                    }
                  />
                  {showLimit && dataLimitGo && (
                    <ReferenceLine
                      y={dataLimitGo}
                      stroke={colors.dark}
                      strokeDasharray="4 4"
                      strokeWidth={1.5}
                      label={{
                        value: `Forfait : ${formatYAxis(dataLimitGo)} Go`,
                        position: 'insideTopRight',
                        style: { fontSize: 11, fill: colors.dark, fontWeight: 600 },
                      }}
                    />
                  )}
                  <Area
                    type="monotone"
                    dataKey="value"
                    stroke={colors.main}
                    fill="url(#areaGradient)"
                    strokeWidth={2.5}
                    name={chartLabel}
                    dot={false}
                    activeDot={{ r: 4, fill: colors.main, stroke: '#fff', strokeWidth: 2 }}
                  />
                </AreaChart>
              )}
            </ChartContainer>
          </div>

          {/* Custom legend */}
          <div className="flex items-center justify-center gap-2 pt-3 border-t border-slate-100 mt-2">
            <span
              className="h-2.5 w-2.5 rounded-full"
              style={{ backgroundColor: colors.main }}
            />
            <span className="text-xs text-slate-500">{chartLabel}</span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ─── Summary card builder ───────────────────────────────

function buildSummaryCards(
  data: AnalyticsUsageResponse | undefined,
  productType: string,
) {
  if (!data) return [];

  const cards: Array<{
    label: string;
    value: string;
    icon: typeof Phone;
    iconBg: string;
    iconColor: string;
  }> = [];

  if (productType === 'voice' && data.voiceSummary) {
    cards.push(
      {
        label: "Nombre d'appels",
        value: formatNumber(data.voiceSummary.totalCalls),
        icon: Phone,
        iconBg: 'bg-emerald-50',
        iconColor: 'text-emerald-600',
      },
      {
        label: 'Durée totale',
        value: data.voiceSummary.totalDuration,
        icon: Clock,
        iconBg: 'bg-emerald-50',
        iconColor: 'text-emerald-600',
      },
      {
        label: 'Durée moyenne',
        value: data.voiceSummary.avgCallDuration,
        icon: Clock,
        iconBg: 'bg-sky-50',
        iconColor: 'text-sky-600',
      },
    );
  }

  if (productType === 'data' && data.dataSummary) {
    cards.push(
      {
        label: 'Consommation totale',
        value: data.dataSummary.totalConsumption,
        icon: HardDrive,
        iconBg: 'bg-sky-50',
        iconColor: 'text-sky-600',
      },
      {
        label: 'Conso moyenne',
        value: data.dataSummary.avgConsumption,
        icon: HardDrive,
        iconBg: 'bg-sky-50',
        iconColor: 'text-sky-600',
      },
    );
  }

  if (productType === 'sms-mms' && data.smsMmsSummary) {
    cards.push({
      label: 'Total SMS / MMS',
      value: formatNumber(data.smsMmsSummary.totalCount),
      icon: MessageSquare,
      iconBg: 'bg-amber-50',
      iconColor: 'text-amber-600',
    });
  }

  return cards;
}
